/**
 * Single animated slide for the How It Works tour.
 *
 * Remounted (keyed by slide index) on every navigation so the entrance
 * choreography replays: the illustration glides in with a slow Ken Burns
 * drift, the kicker/title/body cascade up with a stagger, chips pop last.
 * `direction` flips the entrance so backwards navigation feels like rewind.
 */
import { Box, Chip, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { accentColor } from './tourCore';
import type { TourSlideDef } from './tourSlides';

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(6vw) scale(0.96); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-6vw) scale(0.96); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

const kenBurns = keyframes`
  from { transform: scale(1); }
  to   { transform: scale(1.07) translate(-0.6%, -0.8%); }
`;

const chipPop = keyframes`
  from { opacity: 0; transform: scale(0.7); }
  to   { opacity: 1; transform: scale(1); }
`;

interface TourSlideProps {
  slide: TourSlideDef;
  direction: 1 | -1;
}

export const TourSlide = ({ slide, direction }: TourSlideProps) => {
  const theme = useTheme();
  const accent = accentColor(slide.accent, theme);
  const enterAnim = direction === 1 ? slideInRight : slideInLeft;
  const paleText = alpha(theme.palette.common.white, 0.82);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: { xs: 'column-reverse', md: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 2, md: 6 },
        px: { xs: 3, md: 10 },
        py: { xs: 10, md: 8 },
      }}
    >
      {/* Text column */}
      <Box sx={{ flex: { xs: '0 0 auto', md: '0 1 44%' }, maxWidth: 620, minWidth: 0 }}>
        <Typography
          sx={{
            animation: `${riseIn} 0.6s ease-out both`,
            color: slide.accent === 'night' ? theme.schedule.tentative : alpha(accent, 0.95),
            filter: 'brightness(1.35)',
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            mb: 1.5,
          }}
        >
          {slide.kicker}
        </Typography>
        <Typography
          component="h1"
          sx={{
            animation: `${riseIn} 0.7s 0.12s ease-out both`,
            color: theme.palette.common.white,
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: '-0.02em',
            fontSize: { xs: '2rem', sm: '2.6rem', md: '3.4rem' },
            mb: 2.5,
            textWrap: 'balance',
          }}
        >
          {slide.title}
        </Typography>
        {slide.lines.map((line, i) => (
          <Typography
            key={i}
            sx={{
              animation: `${riseIn} 0.7s ease-out both`,
              animationDelay: `${0.28 + i * 0.16}s`,
              color: paleText,
              fontSize: { xs: '0.95rem', md: '1.15rem' },
              lineHeight: 1.65,
              mb: 1.75,
              maxWidth: 560,
            }}
          >
            {line}
          </Typography>
        ))}
        {slide.chips && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.5 }}>
            {slide.chips.map((chip, i) => (
              <Chip
                key={chip}
                label={chip}
                size="small"
                sx={{
                  animation: `${chipPop} 0.45s ease-out both`,
                  animationDelay: `${0.7 + i * 0.12}s`,
                  backgroundColor: alpha(accent, 0.28),
                  color: theme.palette.common.white,
                  border: `1px solid ${alpha(accent, 0.55)}`,
                  fontWeight: 600,
                  filter: 'brightness(1.15)',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Illustration column */}
      <Box
        sx={{
          flex: { xs: '0 1 auto', md: '0 1 56%' },
          width: '100%',
          maxWidth: { xs: 520, md: 860 },
          minWidth: 0,
          animation: `${enterAnim} 0.75s cubic-bezier(0.22, 0.9, 0.3, 1) both`,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            borderRadius: `${theme.border.radius * 4}px`,
            overflow: 'hidden',
            boxShadow: `0 24px 80px ${alpha(theme.palette.common.black, 0.5)}`,
            border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
            // Soft accent glow behind the art
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              boxShadow: `inset 0 0 120px ${alpha(accent, 0.18)}`,
            },
          }}
        >
          <Box
            component="img"
            src={slide.image}
            alt={slide.alt}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              // Missing artwork should never break the story — hide the frame.
              (e.currentTarget.parentElement as HTMLElement).style.display = 'none';
            }}
            sx={{
              display: 'block',
              width: '100%',
              height: { xs: '32vh', md: '58vh' },
              maxHeight: 560,
              objectFit: 'cover',
              animation: `${kenBurns} 16s ease-in-out both alternate infinite`,
              willChange: 'transform',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
